import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Params, RouterModule } from '@angular/router';
import { ModalService } from "@indice/ng-components";
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { User } from "oidc-client-ts";
import { catchError, debounceTime, distinctUntilChanged, filter, forkJoin, map, of, switchMap, tap } from 'rxjs';
import {
  FloorType,
  GetWorkflowForPropertyResponse,
  PortalApiService,
  PropertyDetails,
  PropertyListingDetails,
  PropertyListingDetailsUpsertRequest,
  SendForReviewResponse
} from 'src/app/services/portal-api.service';
import { ToasterService, ToastType } from 'src/app/services/toaster.service';
import { WorkflowApiService } from 'src/app/services/workflow.service';
import { AuthService } from "../../core/services/auth.service";
import { ExpertsCallbackComponent } from "../../shared/components/experts-callback/experts-callback.component";
import {
  RequestCallbackModalComponent
} from "../../shared/components/request-callback-modal/request-callback-modal.component";
import { IAsideModalResponse } from "../property-details/aside-content/aside-content.component";
import { BasicInformationComponent } from './basic-information/basic-information.component';
import { ConstructionComponent } from './construction/construction.component';
import { ContentComponent } from './content/content.component';
import { FeaturesComponent } from './features/features.component';
import { NavigationComponent } from "./navigation/navigation.component";
import { OwnershipComponent } from './ownership/ownership.component';
import { SurroundingsComponent } from './surroundings/surroundings.component';
import { Media, PhotosComponent } from './photos/photos.component';
import { OverviewUploadPhotosComponent } from "../my-profile/selling/selling-details/overview/overview-upload-photos/overview-upload-photos.component";
import { FloorTypeOptions } from '../property-upload-wizard/appartment-options/apartment-options.component';

@Component({
  selector: 'app-property-listing',
  standalone: true,
  imports: [RouterModule, CommonModule, BasicInformationComponent, ContentComponent, FeaturesComponent, ConstructionComponent, SurroundingsComponent, OwnershipComponent, PhotosComponent, TranslocoDirective, NavigationComponent, ExpertsCallbackComponent, OverviewUploadPhotosComponent],
  templateUrl: './property-listing.component.html',
  styleUrl: './property-listing.component.css'
})
export class PropertyListingComponent implements OnInit {
  public propertyCode: string | undefined;
  protected propertyId: string | undefined;

  protected PropertyListingTabI = PropertyListingTab;
  public activeTab: PropertyListingTab = PropertyListingTab.BasicInfo;
  public propertyListingTab = PropertyListingTab;
  public listingDetails: PropertyListingDetails = new PropertyListingDetails();
  public listingForm: FormGroup<PropertyListingForm> | undefined;
  public tabsComplete = {
    basicInfo: false,
    content: false,
    features: false,
    construction: false,
    surroundings: false,
    ownership: false,
    photos: false,

  }
  public basicInfoComplete = false;
  showDrawer: boolean = false;
  protected sendForReviewButtonEnabled: boolean = false;
  private assetOnboardWorkflowId: string | undefined;
  private currentUser: User | null;
  public property: PropertyDetails | undefined;
  public selectedCoverPhoto:Media  = new Media({ isDefault: true });
  public selectedSecondaryImages : Media[]= [];
  private toastTranslations:any

  constructor(
    private readonly _activatedRoute: ActivatedRoute,
    private _portalApi: PortalApiService,
    private _workflowApi: WorkflowApiService,
    private _modalService: ModalService,
    private _authService: AuthService,
    private _translate : TranslocoService,
    @Inject(ToasterService) private _toastService: ToasterService
  ) {
    this._activatedRoute.params.subscribe((params: Params) => {
      this.propertyCode = params['propertyCode'];
    });

    this.currentUser = this._authService.getCurrentUser();
    this._translate.selectTranslateObject('toast-workflows-messages').pipe(
      tap((translations) => {
        this.toastTranslations = translations;
      })
    ).subscribe();
  }

  ngOnInit(): void {
    if (this.propertyCode) { // Only need propertyCode initially
      this._portalApi.getListingDetails(this.propertyCode).pipe(
        catchError((error: any) => {
          const errorDetail = error.errors?.InitiateWorkflow?.filter((f: any) => f)
            .join(', ') ?? undefined;
    
          this._toastService.showBanner(ToastType.Error, this._translate.translate('Failed to get property'), errorDetail, 10000);
          return of(undefined);
        }),
        filter((f): f is PropertyListingDetails => !!f),
        tap((listingDetails: PropertyListingDetails) => {
          this.listingDetails = listingDetails;
          this.initFormGroup();
          this.listingForm?.patchValue(this.listingDetails);
          console.log( this.listingForm)
          this.setProggress();
          this.initAutoSave();
        }),
        switchMap((listingDetails: PropertyListingDetails) => {
          const propertyId = listingDetails.propertyId ?? '';
          // Now you have propertyId from the first call
          
          // Make both API calls in parallel using forkJoin
          return forkJoin([
            this._workflowApi.assetOnboardGetWorkflowForReview(propertyId),
            this._portalApi.getPropertyById(propertyId) 
          ]);
        }),
        catchError((error: any) => {
          const errorDetail = error.errors?.InitiateWorkflow?.filter((f: any) => f)
            .join(', ') ?? undefined;
    
          // this._toastService.show(ToastType.Error, 'Failed to get workflow', errorDetail, 10000);
          return of(undefined);
        }),
        filter((results): results is [GetWorkflowForPropertyResponse, any] => !!results), // Adjust type as needed
        tap(([workflowResponse, propertyResponse]) => {
          if (workflowResponse.canSendForReview) {
            this.sendForReviewButtonEnabled = true;
          }
          this.property = propertyResponse; // Assign property from second API call
        })
      ).subscribe();
    }
  }

  getCoverPhoto(event:any){
    this.selectedCoverPhoto = event
  }

  getSecondaryPhotos(event:any){
    this.selectedSecondaryImages = event
  }


  public onNextTab(tab: PropertyListingTab) {
    this.activeTab = tab;
    window.scroll({ top: 0, left: 0, behavior: 'smooth' });
  }

  public setPhotosProggress(isComplete: boolean) {
    this.tabsComplete.photos = isComplete;
  }

  onSubmitForReview() {
    if (!this.listingDetails.propertyId) {
      return;
    }


    this.sendForReviewButtonEnabled = false;
    this._workflowApi.assetOnboardSendForReview(this.listingDetails.propertyId).pipe(
      catchError((error: any) => {
        this.sendForReviewButtonEnabled = true;
        const errorDetail = error.errors?.InitiateWorkflow?.filter((f: any) => f)
          .join(', ') ?? undefined;

        this._toastService.showBanner(ToastType.Error, 'Failed to submit for review', errorDetail, 10000);
        return of(undefined)
      }),
      filter((f): f is SendForReviewResponse => !!f), // Filter out undefined values
      tap((t: SendForReviewResponse) => {
        var msg = `RM name: ${t.fullName}, Email: ${t.email}, Phone: ${t.phone}`;
        this._toastService.showBanner(ToastType.Success, 'Submitted for Review ', msg, 10000);

        this.sendForReviewButtonEnabled = false;

      })
    ).subscribe();
  }

  public onCallbackClick() {
    const modal = this._modalService.show(RequestCallbackModalComponent, {
      keyboard: true,
      class: 'h-dvh md:h-auto',
      initialState: {
        property: this.property,
        isLoggedIn: this.currentUser !== null,
      },
    });
    modal.onHidden?.pipe(
      tap((t: any) => {
        let input = t.result as IAsideModalResponse;
        if (input.success != undefined) {
          if (input.success) {
            this._toastService.showBanner(ToastType.Success,
              this.toastTranslations?.callback?.success?.title ?? '', 
              (this.toastTranslations?.callback?.success?.title ?? '' as string)
              .replace('{{fullName}}', this.property?.brokerName ?? '') 
              .replace('{{phoneNumber}}', this.currentUser?.profile?.phone_number ?? ''))
          } else {
            this._toastService.showBanner(ToastType.Error,this.toastTranslations?.callback?.error?.title ?? '', input.errorDesc, 10000);
          }
        }
      })
    ).subscribe();
  }

  public currentStepLabel(): string {
    switch (this.activeTab) {
      case PropertyListingTab.BasicInfo:
        return "tabs.basicInformation"
      case PropertyListingTab.Features:
        return "tabs.features"
      case PropertyListingTab.Construction:
        return "tabs.construction"
      case PropertyListingTab.Surroundings:
        return "tabs.surroundings"
      case PropertyListingTab.Ownership:
        return "tabs.ownership"
      case PropertyListingTab.Photos:
        return "tabs.photos"
      default:
        return ""
    }
  }

  public handleDrawer() {
    this.showDrawer = !this.showDrawer;
  }

  handleNext() {
    switch (this.activeTab) {
      case PropertyListingTab.BasicInfo:
        this.onNextTab(PropertyListingTab.Features);
        break;
      case PropertyListingTab.Features:
        this.onNextTab(PropertyListingTab.Construction);
        break;
      case PropertyListingTab.Construction:
        this.onNextTab(PropertyListingTab.Surroundings);
        break;
      case PropertyListingTab.Surroundings:
        this.onNextTab(PropertyListingTab.Ownership);
        break;
      case PropertyListingTab.Ownership:
        this.onNextTab(PropertyListingTab.Photos);
        break;
      default:
        this.onNextTab(PropertyListingTab.BasicInfo);
        break;
    }
  }

  handlePrev() {
    switch (this.activeTab) {
      case PropertyListingTab.Features:
        this.onNextTab(PropertyListingTab.BasicInfo);
        break;
      case PropertyListingTab.Construction:
        this.onNextTab(PropertyListingTab.Features);
        break;
      case PropertyListingTab.Surroundings:
        this.onNextTab(PropertyListingTab.Construction);
        break;
      case PropertyListingTab.Ownership:
        this.onNextTab(PropertyListingTab.Surroundings);
        break;
      case PropertyListingTab.Photos:
        this.onNextTab(PropertyListingTab.Ownership);
        break;
      default:
        this.onNextTab(PropertyListingTab.BasicInfo);
    }
  }

  handleSkip() {
    this.handleNext();
  }

  private initFormGroup() {
    this.listingForm = new FormGroup(new PropertyListingForm());
  }

  private setProggress() {
    this.tabsComplete.basicInfo = (this.listingForm?.controls?.basicInfo.valid && this.listingForm?.controls.basicInfo.controls.address.valid) ?? false;
    this.tabsComplete.content = (this.listingForm?.controls?.content.valid) ?? false;
    this.tabsComplete.features = (this.listingForm?.controls?.features.valid) ?? false;
    this.tabsComplete.construction = (this.listingForm?.controls?.construction.valid) ?? false;
    this.tabsComplete.surroundings =  (this.listingForm?.controls?.surroundings.valid) ?? false;
    this.tabsComplete.ownership = (this.listingForm?.controls.ownership.valid) ?? false;
  }

  private initAutoSave() {
    this.listingForm?.valueChanges
      .pipe(
        debounceTime(3000),
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
        switchMap(() => {
          return this._portalApi.updateListingDetails(PropertyListingDetailsUpsertRequest.fromJS(this.listingForm?.getRawValue())).pipe(
            switchMap(() => {
              return this._portalApi.getListingDetails(this.propertyCode ?? '').pipe(
                map((res: PropertyListingDetails) => {
                  this.listingForm?.patchValue(res)
                  this.setProggress();
                })
              )
            })
          )
        })
      ).subscribe();
  }
}

export class PropertyListingForm {
  propertyId: FormControl = new FormControl<string | null>(null);
  basicInfo: FormGroup<BasicInfoFormGroup> = new FormGroup(new BasicInfoFormGroup())
  content: FormGroup<ContentFormGroup> = new FormGroup(new ContentFormGroup())
  features: FormGroup<FeaturesFormGroup> = new FormGroup(new FeaturesFormGroup())
  construction: FormGroup<ConstructionFormGroup> = new FormGroup(new ConstructionFormGroup())
  surroundings: FormGroup<SurroundingsFormGroup> = new FormGroup(new SurroundingsFormGroup())
  ownership: FormGroup<OwnershipFormGroup> = new FormGroup(new OwnershipFormGroup())
}

export class BasicInfoFormGroup {
  constructionYear: FormControl = new FormControl<number | null>(null, Validators.required)
  askingPrice: FormControl = new FormControl<number | null>(null, Validators.required)
  lowestPrice: FormControl = new FormControl<number | null>(null, Validators.required)
  type: FormControl = new FormControl<string | null>(null, Validators.required)
  area: FormControl = new FormControl<number | null>(null, Validators.required)
  address: FormGroup<AddressFormGroup> = new FormGroup(new AddressFormGroup())
}

export class AddressFormGroup {
  country: FormControl = new FormControl<string | null>(null)
  prefecture: FormControl = new FormControl<string | null>(null, Validators.required)
  administrativeAreas: FormControl = new FormControl<string | null>(null)
  streetName: FormControl = new FormControl<string | null>(null, Validators.required)
  streetNumber: FormControl = new FormControl<string | null>(null, Validators.required)
  postalCode: FormControl = new FormControl<string | null>(null, Validators.required)
  geoPoint: FormControl = new FormControl<string | null>(null, Validators.required)
}

export class ContentFormGroup {
  titleEl: FormControl = new FormControl<string | null>(null, [Validators.maxLength(1024), Validators.required])
  shortDescriptionEl: FormControl = new FormControl<string | null>(null, Validators.required)
  titleEn: FormControl = new FormControl<string | null>(null, [Validators.maxLength(1024), Validators.required])
  shortDescriptionEn: FormControl = new FormControl<string | null>(null, Validators.required)
}

export class FeaturesFormGroup {
  numberOfRooms: FormControl = new FormControl<number | null>(null, Validators.required)
  numberOfBathrooms: FormControl = new FormControl<number | null>(null, Validators.required)
  //numberOfWC: FormControl = new FormControl<number | null>(null, Validators.required)
  amenities: FormGroup<AmenitiesFormGroup> = new FormGroup(new AmenitiesFormGroup())
  parkingType: FormControl = new FormControl<string[]>([])
  parkingSpace: FormControl = new FormControl<number | null>(null)
  storageAreaAmount: FormControl = new FormControl<number | null>(null)
  storageArea: FormControl = new FormControl<number | null>(400)
  storageType: FormControl = new FormControl<string[]>([])
  floor: FormControl = new FormControl<number | null>(null, Validators.required)
  floorType: FormControl = new FormControl<FloorType | null>(null)
}

export class AmenitiesFormGroup {
  accessibilityForDisabled: FormControl = new FormControl<boolean | null>(null)
  internalStaircase:FormControl = new FormControl<boolean | null>(null)
  hasGarden:FormControl = new FormControl<boolean | null>(null)
  hasPool:FormControl = new FormControl<boolean | null>(null)
  hasFirePlace:FormControl = new FormControl<boolean | null>(null)
  hasSafetyDoor:FormControl = new FormControl<boolean | null>(null)
  hasElevator:FormControl = new FormControl<boolean | null>(null)
  hasGym:FormControl = new FormControl<boolean | null>(null)
  hasAirconditioning:FormControl = new FormControl<boolean | null>(null)
  hasSeaView:FormControl = new FormControl<boolean | null>(null)
  hasAlarm:FormControl = new FormControl<boolean | null>(null)
  hasGreatView:FormControl = new FormControl<boolean | null>(null)
}

export class ConstructionFormGroup {
  heatingType: FormControl = new FormControl<string | null>(null);
  heatingControl: FormControl = new FormControl<string | null>(null);
  energyClass: FormControl = new FormControl<string | null>(null);
  constructionYear: FormControl = new FormControl<number | null>(null, Validators.required);
  constructionMaterial: FormControl = new FormControl<string[]>([]);
  floorMaterial: FormControl = new FormControl<string[]>([]);
  condition: FormControl = new FormControl<string | null>(null, Validators.required);
}

export class SurroundingsFormGroup {
  plotArea: FormControl = new FormControl<number | null>(null);
  floor: FormControl = new FormControl<number | null>(null);
  view: FormControl = new FormControl<string[]>([]);
  orientation: FormControl = new FormControl<string | null>(null, Validators.required);
  surrounding: FormControl = new FormControl<string[]>([]);
  nearbyHighwaysEl: FormControl = new FormControl<string | null>(null);
  nearbyHighwaysEn: FormControl = new FormControl<string | null>(null);
}

export class OwnershipFormGroup {
  kaek: FormControl = new FormControl<string | null>(null);
  usageCategory: FormControl = new FormControl<string | null>(null, Validators.required);
  ownershipType: FormControl = new FormControl<string | null>(null);
  ownershipPercentage: FormControl = new FormControl<number | null>(null);
  availability: FormControl = new FormControl<string | null>(null);
}

export enum PropertyListingTab {
  BasicInfo = 1,
  Content = 2,
  Features = 3,
  Construction = 4,
  Surroundings = 5,
  Ownership = 6,
  Photos = 7
}
